import React, { ChangeEvent, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { useNavigate } from 'react-router'
import {
    Row, Col, Card, CardBody, Input, Label, ToggleSwitch, InfoBox, Text, Badge, Icon, Button, useUI
} from '@evertel/web/ui'
import { DocumentIcon } from '../elements/DocumentIcon'
import { DocIconPickerModal } from './elements/DocIconPickerModal'
import { SchemaBuilderController } from '../../controller'
import SchemaCategorySearchSelect from '../schema-categories/SchemaCategorySearchSelect'
import { APIDataDocumentSchemaCategory } from '@evertel/types'
import { stringToHslColor } from '@evertel/utils'

interface SettingsBuilderSettingsProps {
    schemaBuilderController: SchemaBuilderController
    departmentId: number
}

const SchemaBuilderSettings: React.FC<SettingsBuilderSettingsProps> = observer(({
    schemaBuilderController,
    departmentId
}) => {

    const { addToast, isConfirmed } = useUI()
    const navigate = useNavigate()

    const [showDocIconPickerModal, setShowDocIconPickerModal] = useState(false)

    const onChangeName = (e: ChangeEvent<HTMLInputElement>) => {
        schemaBuilderController.setName(e.target.value)
    }

    const onChangeDescription = (e: ChangeEvent<HTMLInputElement>) => {
        schemaBuilderController.setDescription(e.target.value)
    }

    const onDeleteSchema = async () => {
        const deleteIt = () => {
            try {
                schemaBuilderController.deleteSchema()

                addToast({
                    color: 'success',
                    title: 'Template Deleted',
                    message: 'Your template has been successfully deleted.'
                })

                navigate('/everdocs')

            } catch (error: any) {
                addToast({
                    color: 'danger',
                    title: 'Oops!',
                    message: error.message
                })
            }
        }

        const confirmed = await isConfirmed({
            title: 'Delete Template?',
            message: 'Are you sure you want to delete this template?',
            acceptButton: {
                label: 'Delete',
                color: 'danger'
            }
        })
        if (confirmed) {
            deleteIt()
        }
    }

    return (
        <Row className="mt-4">
            <Col md={6}>
                <Card style={{minHeight: '75vh'}}>
                    <CardBody>
                        <Col>
                            <Row
                                valign="center"
                                className="mb-3">
                                <DocumentIcon
                                    schema={{
                                        icon: schemaBuilderController.icon,
                                        color: schemaBuilderController.color
                                    }}
                                    width={45}
                                    height={45}
                                    onClick={() => setShowDocIconPickerModal(true)}
                                    className="mx-3"
                                />
                                <Label
                                    text="Template Name"
                                    htmlFor="name"
                                    className="w-100">
                                    <Input
                                        id="name"
                                        name="name"
                                        value={schemaBuilderController.name}
                                        onChange={onChangeName}
                                    />
                                </Label>
                            </Row>
                            <Label
                                text="Description"
                                htmlFor="description">
                                <Input
                                    id="description"
                                    name="description"
                                    placeholder="Add a short description..."
                                    value={schemaBuilderController.description}
                                    onChange={onChangeDescription}
                                />
                            </Label>
                            <ToggleSwitch
                                id="isEnabled"
                                label="Enabled"
                                checked={schemaBuilderController.isEnabled}
                                className="mt-2"
                                onChange={() => schemaBuilderController.setIsEnabled(!schemaBuilderController.isEnabled)}
                            />
                            {(!schemaBuilderController.isEnabled) &&
                                <InfoBox
                                    color="info"
                                    className="mt-2">
                                    When an EverDoc template is disabled, it does not effect any existing documents created from this template.
                                    It will remove this EverDoc template as an option when creating new documents however.
                                </InfoBox>
                            }
                            {(!!schemaBuilderController.id) &&
                            <Row
                                align="between"
                                className="mt-5">
                                <Text>
                                    This template is used in <Text bold>{schemaBuilderController.documentsCount}</Text> documents
                                </Text>
                                {(!schemaBuilderController.documentsCount) &&
                                    <Button
                                        color="transparent"
                                        onClick={onDeleteSchema}>
                                        <Text color="danger">
                                            Delete this template
                                        </Text>
                                    </Button>
                                }
                            </Row>
                            }
                        </Col>
                    </CardBody>
                </Card>
            </Col>
            <Col valign="top">
                <Card>
                    <CardBody>
                        <Text bold>
                            Add this template to one or more categories
                        </Text>
                        <SchemaCategorySearchSelect
                            onSelect={(category) => schemaBuilderController.addCategory(category as APIDataDocumentSchemaCategory)}
                            departmentId={departmentId}
                        />
                        <Col className="mt-4">
                            <Text bold>
                                Categories
                            </Text>
                            {schemaBuilderController.categories?.map((cat) => {
                                if (!cat?.id) return null
                                return (
                                    <div key={cat.id}>
                                        <Badge
                                            size={14}
                                            style={{backgroundColor: stringToHslColor(cat.name as string)}}
                                            className="mb-1">
                                            {cat.name}
                                            <Button
                                                className="ms-3 p-0 border-0 bg-transparent"
                                                onClick={() => schemaBuilderController.removeCategory(cat.id as number)}
                                                title="Remove category"
                                                aria-label={`Remove ${cat.name}`}
                                            >
                                                <Icon
                                                    name="xmark"
                                                    color="white"
                                                />
                                            </Button>
                                        </Badge>
                                    </div>
                                )
                            })}
                        </Col>
                    </CardBody>
                </Card>
            </Col>

            <DocIconPickerModal
                visible={showDocIconPickerModal}
                onClose={() => setShowDocIconPickerModal(false)}
                controller={schemaBuilderController}
            />
        </Row>
    )
})

export {
    SchemaBuilderSettings
}
